/**
 * Account - User account page with order and portrait history
 * Features:
 * - Profile overview
 * - Order history with dummy data
 * - Portrait history with dummy data
 * - Account settings placeholder
 */
import React, { useState, useEffect } from 'react';
import { FONT_FAMILY, SHOP_SPACING } from '../ShopStyles';
import { useAuth } from '../context/AuthContext';
import { useSiteAccess } from '../../SiteAccessContext';

// Dummy pet portraits
const DUMMY_PORTRAITS = [
  {
    id: 'p1',
    petName: 'Temu',
    originalImage: 'https://images.unsplash.com/photo-1574158622682-e40e69881006?w=200&h=200&fit=crop',
    generatedImage: 'https://images.unsplash.com/photo-1574158622682-e40e69881006?w=400&h=400&fit=crop',
    style: 'Movie Poster',
    artStyle: 'Cinematic',
    background: 'Dramatic Lighting',
    createdAt: '2024-01-15',
    usedOnProducts: 3,
  },
  {
    id: 'p2',
    petName: 'Temu',
    originalImage: 'https://images.unsplash.com/photo-1574158622682-e40e69881006?w=200&h=200&fit=crop',
    generatedImage: 'https://images.unsplash.com/photo-1574158622682-e40e69881006?w=400&h=400&fit=crop',
    style: 'Pirate',
    artStyle: 'Watercolor',
    background: 'Ocean',
    createdAt: '2024-01-10',
    usedOnProducts: 2,
  },
  {
    id: 'p3',
    petName: 'Luna',
    originalImage: 'https://images.unsplash.com/photo-1543466835-00a7907e9de1?w=200&h=200&fit=crop',
    generatedImage: 'https://images.unsplash.com/photo-1543466835-00a7907e9de1?w=400&h=400&fit=crop',
    style: 'Superhero',
    artStyle: 'Comic Book',
    background: 'City Skyline',
    createdAt: '2024-01-08',
    usedOnProducts: 1,
  },
];

// Dummy orders
const DUMMY_ORDERS = [
  {
    id: 'ORD-2024-001',
    date: 'January 15, 2024',
    status: 'Delivered',
    statusColor: '#22c55e',
    total: 74.97,
    items: [
      { name: 'Unisex T-Shirt', variant: 'Black / M', petName: 'Temu', image: 'https://via.placeholder.com/60x60?text=T', price: 24.99 },
      { name: 'Canvas Print', variant: '16×20 inch', petName: 'Temu', image: 'https://via.placeholder.com/60x60?text=C', price: 34.99 },
      { name: 'Mug', variant: 'White / 11oz', petName: 'Temu', image: 'https://via.placeholder.com/60x60?text=M', price: 14.99 },
    ],
  },
  {
    id: 'ORD-2024-002',
    date: 'January 10, 2024',
    status: 'Shipped',
    statusColor: '#f59e0b',
    total: 49.98,
    items: [
      { name: 'Phone Case', variant: 'iPhone 15', petName: 'Luna', image: 'https://via.placeholder.com/60x60?text=P', price: 24.99 },
      { name: 'Stickers Pack', variant: '6 stickers', petName: 'Luna', image: 'https://via.placeholder.com/60x60?text=S', price: 9.99 },
      { name: 'Pillow', variant: '18×18 inch', petName: 'Temu', image: 'https://via.placeholder.com/60x60?text=Pi', price: 14.99 },
    ],
  },
  {
    id: 'ORD-2024-003',
    date: 'December 28, 2023',
    status: 'Delivered',
    statusColor: '#22c55e',
    total: 39.98,
    items: [
      { name: 'Poster Print', variant: '18×24 inch', petName: 'Temu', image: 'https://via.placeholder.com/60x60?text=Po', price: 19.99 },
      { name: 'Hat', variant: 'Navy / Adjustable', petName: 'Temu', image: 'https://via.placeholder.com/60x60?text=H', price: 19.99 },
    ],
  },
];

const Account = ({ onNavigate }) => {
  const { user, isLoggedIn, logout, openLoginModal } = useAuth();
  const { isSiteGated, signOutOfSite } = useSiteAccess();
  const [activeTab, setActiveTab] = useState('portraits');
  
  // Inject animations - must be before any early returns
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'account-animations';
    styleSheet.textContent = `
      @keyframes slideIn {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-5px); }
      }
      .account-card {
        animation: slideIn 0.4s ease-out forwards;
        opacity: 0;
      }
      .account-tab:hover {
        transform: translateY(-2px);
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      const existing = document.getElementById('account-animations');
      if (existing) existing.remove();
    };
  }, []);
  
  // Redirect to login if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      openLoginModal();
      onNavigate?.('home');
    }
  }, [isLoggedIn, openLoginModal, onNavigate]);
  
  if (!isLoggedIn || !user) {
    return null;
  }

  const tabs = [
    { id: 'portraits', label: '🎨 My Portraits', count: DUMMY_PORTRAITS.length },
    { id: 'orders', label: '📦 Order History', count: DUMMY_ORDERS.length },
    { id: 'settings', label: '⚙️ Settings', count: null },
  ];

  return (
    <div style={styles.container}>
      {/* Profile Header */}
      <div style={styles.profileHeader}>
        <div style={styles.avatarSection}>
          <div style={styles.avatar}>{user.avatar}</div>
          <div style={styles.profileInfo}>
            <h1 style={styles.userName}>{user.name}</h1>
            <p style={styles.userEmail}>{user.email}</p>
            <p style={styles.memberSince}>Member since {user.memberSince}</p>
          </div>
        </div>
        
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <span style={styles.statNumber}>{DUMMY_PORTRAITS.length}</span>
            <span style={styles.statLabel}>Portraits</span>
          </div>
          <div style={styles.statCard}>
            <span style={styles.statNumber}>{DUMMY_ORDERS.length}</span>
            <span style={styles.statLabel}>Orders</span>
          </div>
          <div style={styles.statCard}>
            <span style={styles.statNumber}>2</span>
            <span style={styles.statLabel}>Pets</span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabBar}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="account-tab"
            style={{
              ...styles.tabBtn,
              ...(activeTab === tab.id ? styles.tabBtnActive : {}),
            }}
          >
            {tab.label}
            {tab.count !== null && (
              <span style={styles.tabCount}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={styles.tabContent}>
        {/* Portraits Tab */}
        {activeTab === 'portraits' && (
          <div style={styles.portraitsGrid}>
            {DUMMY_PORTRAITS.map((portrait, idx) => (
              <div 
                key={portrait.id} 
                className="account-card"
                style={{...styles.portraitCard, animationDelay: `${idx * 0.1}s`}}
              >
                <div style={styles.portraitImageWrapper}>
                  <img src={portrait.generatedImage} alt={portrait.petName} style={styles.portraitImage} />
                  <div style={styles.portraitBadge}>
                    {portrait.usedOnProducts} product{portrait.usedOnProducts !== 1 ? 's' : ''}
                  </div>
                </div>
                <div style={styles.portraitInfo}>
                  <h3 style={styles.portraitName}>{portrait.petName}</h3>
                  <p style={styles.portraitStyle}>
                    <span style={styles.styleTag}>{portrait.style}</span>
                    <span style={styles.artTag}>{portrait.artStyle}</span>
                  </p>
                  <p style={styles.portraitDate}>Created {portrait.createdAt}</p>
                </div>
                <div style={styles.portraitActions}>
                  <button 
                    style={styles.usePortraitBtn}
                    onClick={() => onNavigate?.('products')}
                  >
                    🛍️ Use on Product
                  </button>
                  <button style={styles.viewPortraitBtn}>
                    🔍 View
                  </button>
                </div>
              </div>
            ))}
            
            {/* Create New Card */}
            <div 
              className="account-card"
              style={{...styles.createNewCard, animationDelay: `${DUMMY_PORTRAITS.length * 0.1}s`}}
              onClick={() => onNavigate?.('upload')}
            >
              <span style={styles.createIcon}>✨</span>
              <span style={styles.createText}>Create New Portrait</span>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div style={styles.ordersContainer}>
            {DUMMY_ORDERS.map((order, idx) => (
              <div 
                key={order.id} 
                className="account-card"
                style={{...styles.orderCard, animationDelay: `${idx * 0.1}s`}}
              >
                <div style={styles.orderHeader}>
                  <div>
                    <span style={styles.orderId}>{order.id}</span>
                    <span style={styles.orderDate}>{order.date}</span>
                  </div>
                  <span style={{...styles.orderStatus, background: order.statusColor}}>
                    {order.status}
                  </span>
                </div>
                
                <div style={styles.orderItems}>
                  {order.items.map((item, i) => (
                    <div key={i} style={styles.orderItem}>
                      <div style={styles.orderItemImage}>
                        <img src={item.image} alt={item.name} style={styles.orderItemImg} />
                      </div>
                      <div style={styles.orderItemInfo}>
                        <span style={styles.orderItemName}>{item.name}</span>
                        <span style={styles.orderItemVariant}>{item.variant}</span>
                        <span style={styles.orderItemPet}>🐾 {item.petName}'s portrait</span>
                      </div>
                      <span style={styles.orderItemPrice}>${item.price.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                
                <div style={styles.orderFooter}>
                  <span style={styles.orderTotal}>Total: <strong>${order.total.toFixed(2)}</strong></span>
                  <button style={styles.trackOrderBtn}>📦 Track Order</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div style={styles.settingsContainer}>
            <div className="account-card" style={styles.settingsCard}>
              <h3 style={styles.settingsTitle}>👤 Profile Settings</h3>
              <div style={styles.settingsForm}>
                <label style={styles.settingsLabel}>
                  Name
                  <input type="text" defaultValue={user.name} style={styles.settingsInput} />
                </label>
                <label style={styles.settingsLabel}>
                  Email
                  <input type="email" defaultValue={user.email} style={styles.settingsInput} />
                </label>
                <button style={styles.saveBtn}>💾 Save Changes</button>
              </div>
            </div>
            
            <div className="account-card" style={{...styles.settingsCard, animationDelay: '0.1s'}}>
              <h3 style={styles.settingsTitle}>🔔 Notifications</h3>
              <div style={styles.settingsOptions}>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" defaultChecked style={styles.checkbox} />
                  Order updates
                </label>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" defaultChecked style={styles.checkbox} />
                  New styles available
                </label>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" style={styles.checkbox} />
                  Marketing emails
                </label>
              </div>
            </div>
            
            <div className="account-card" style={{...styles.settingsCard, animationDelay: '0.2s'}}>
              <h3 style={styles.settingsTitle}>🚪 Account</h3>
              {isSiteGated && (
                <p style={styles.siteGateHint}>
                  This shop is on a private link. You can clear the site session without signing out of your
                  account.
                </p>
              )}
              {isSiteGated && (
                <button
                  type="button"
                  style={styles.siteGateBtn}
                  onClick={() => {
                    signOutOfSite();
                  }}
                >
                  End private site session
                </button>
              )}
              <button
                style={{
                  ...styles.logoutBtn,
                  marginTop: isSiteGated ? 12 : 0,
                }}
                onClick={() => {
                  logout();
                  onNavigate?.('home');
                }}
              >
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Back Button */}
      <button onClick={() => onNavigate?.('home')} style={styles.backBtn}>
        ← Back to Shop
      </button>
    </div>
  );
};

const styles = {
  container: {
    padding: `${SHOP_SPACING.lg} 0`,
    maxWidth: '1000px',
    margin: '0 auto',
  },
  
  // Profile Header
  profileHeader: {
    background: 'linear-gradient(135deg, #faf5ff 0%, #fdf4ff 100%)',
    borderRadius: '24px',
    padding: '32px',
    marginBottom: '32px',
    border: '2px solid #e9d5ff',
  },
  avatarSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginBottom: '24px',
  },
  avatar: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '40px',
    boxShadow: '0 8px 24px rgba(147, 51, 234, 0.3)',
  },
  profileInfo: {
    flex: 1,
  },
  userName: {
    fontSize: '28px',
    fontWeight: '800',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  userEmail: {
    fontSize: '15px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    margin: '4px 0',
  },
  memberSince: {
    fontSize: '13px',
    color: '#9ca3af',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  statsRow: {
    display: 'flex',
    gap: '16px',
  },
  statCard: {
    flex: 1,
    background: '#fff',
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  statNumber: {
    display: 'block',
    fontSize: '28px',
    fontWeight: '800',
    color: '#7c3aed',
    fontFamily: FONT_FAMILY,
  },
  statLabel: {
    fontSize: '13px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
  },
  
  // Tab Bar
  tabBar: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    background: '#f3f4f6',
    borderRadius: '14px',
    padding: '6px',
  },
  tabBtn: {
    flex: 1,
    padding: '14px 20px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    background: 'transparent',
    border: 'none',
    borderRadius: '10px',
    color: '#6b7280',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  tabBtnActive: {
    background: '#fff',
    color: '#7c3aed',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  tabCount: {
    background: '#e9d5ff',
    color: '#7c3aed',
    fontSize: '12px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '10px',
  },
  tabContent: {
    minHeight: '400px',
  },
  
  // Portraits Grid
  portraitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  },
  portraitCard: {
    background: '#fff',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
    transition: 'all 0.3s ease',
  },
  portraitImageWrapper: {
    position: 'relative',
    height: '200px',
    overflow: 'hidden',
  },
  portraitImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  portraitBadge: {
    position: 'absolute',
    bottom: '12px',
    right: '12px',
    background: 'rgba(147, 51, 234, 0.9)',
    color: '#fff',
    fontSize: '12px',
    fontWeight: '600',
    padding: '6px 12px',
    borderRadius: '20px',
    fontFamily: FONT_FAMILY,
  },
  portraitInfo: {
    padding: '16px',
  },
  portraitName: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '8px',
  },
  portraitStyle: {
    display: 'flex',
    gap: '8px',
    marginBottom: '8px',
  },
  styleTag: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#7c3aed',
    background: '#f3e8ff',
    padding: '4px 10px',
    borderRadius: '12px',
    fontFamily: FONT_FAMILY,
  },
  artTag: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#0ea5e9',
    background: '#e0f2fe',
    padding: '4px 10px',
    borderRadius: '12px',
    fontFamily: FONT_FAMILY,
  },
  portraitDate: {
    fontSize: '13px',
    color: '#9ca3af',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  portraitActions: {
    display: 'flex',
    gap: '8px',
    padding: '0 16px 16px',
  },
  usePortraitBtn: {
    flex: 1,
    padding: '10px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    cursor: 'pointer',
  },
  viewPortraitBtn: {
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    background: '#f3f4f6',
    border: 'none',
    borderRadius: '10px',
    color: '#6b7280',
    cursor: 'pointer',
  },
  createNewCard: {
    background: 'linear-gradient(135deg, #faf5ff 0%, #fdf4ff 100%)',
    borderRadius: '20px',
    border: '3px dashed #c4b5fd',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '280px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  createIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  createText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#7c3aed',
    fontFamily: FONT_FAMILY,
  },
  
  // Orders
  ordersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  orderCard: {
    background: '#fff',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #f3f4f6',
  },
  orderId: {
    display: 'block',
    fontSize: '16px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
  },
  orderDate: {
    display: 'block',
    fontSize: '13px',
    color: '#9ca3af',
    fontFamily: FONT_FAMILY,
    marginTop: '4px',
  },
  orderStatus: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#fff',
    padding: '6px 14px',
    borderRadius: '20px',
    fontFamily: FONT_FAMILY,
  },
  orderItems: {
    padding: '16px 20px',
  },
  orderItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 0',
    borderBottom: '1px solid #f9fafb',
  },
  orderItemImage: {
    width: '60px',
    height: '60px',
    borderRadius: '10px',
    overflow: 'hidden',
    background: '#f3f4f6',
  },
  orderItemImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  orderItemInfo: {
    flex: 1,
  },
  orderItemName: {
    display: 'block',
    fontSize: '15px',
    fontWeight: '600',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
  },
  orderItemVariant: {
    display: 'block',
    fontSize: '13px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
  },
  orderItemPet: {
    display: 'block',
    fontSize: '12px',
    color: '#7c3aed',
    fontFamily: FONT_FAMILY,
    marginTop: '4px',
  },
  orderItemPrice: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
  },
  orderFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    background: '#fafafa',
  },
  orderTotal: {
    fontSize: '16px',
    color: '#374151',
    fontFamily: FONT_FAMILY,
  },
  trackOrderBtn: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    cursor: 'pointer',
  },
  
  // Settings
  settingsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  settingsCard: {
    background: '#fff',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
  },
  settingsTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '20px',
  },
  settingsForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  settingsLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    fontFamily: FONT_FAMILY,
  },
  settingsInput: {
    padding: '12px 16px',
    fontSize: '15px',
    fontFamily: FONT_FAMILY,
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    outline: 'none',
  },
  saveBtn: {
    padding: '14px',
    fontSize: '15px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    cursor: 'pointer',
    marginTop: '8px',
  },
  settingsOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '15px',
    color: '#374151',
    fontFamily: FONT_FAMILY,
    cursor: 'pointer',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  siteGateHint: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#4b5563',
    marginBottom: '10px',
    fontFamily: FONT_FAMILY,
  },
  siteGateBtn: {
    padding: '12px 14px',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: FONT_FAMILY,
    background: '#f3e8ff',
    border: '1px solid #c4b5fd',
    borderRadius: '10px',
    color: '#6b21a8',
    cursor: 'pointer',
    width: '100%',
    marginBottom: 4,
  },
  logoutBtn: {
    padding: '14px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    background: '#fee2e2',
    border: 'none',
    borderRadius: '10px',
    color: '#dc2626',
    cursor: 'pointer',
    width: '100%',
  },
  
  // Back Button
  backBtn: {
    display: 'block',
    margin: '40px auto 0',
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    background: '#fff',
    border: '2px solid #9333ea',
    borderRadius: '10px',
    color: '#9333ea',
    cursor: 'pointer',
  },
};

export default Account;
